/**
 * Compresses an image for user-visible content (progress snaps, etc.)
 * - Maintains high quality for viewing
 * - Resizes to max 3840px (4K resolution for crisp display)
 * - JPEG quality 95% (very high quality)
 * - Optimized for mobile displays
 */
export const compressImageForDisplay = async (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.src = e.target?.result as string;
    };

    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      // Calculate new dimensions (max 3840px on longest side for 4K quality)
      const maxSize = 3840;
      let width = img.width;
      let height = img.height;

      if (width > height) {
        if (width > maxSize) {
          height = Math.round((height * maxSize) / width);
          width = maxSize;
        }
      } else {
        if (height > maxSize) {
          width = Math.round((width * maxSize) / height);
          height = maxSize;
        }
      }

      canvas.width = width;
      canvas.height = height;

      // Use high quality settings for sharp rendering
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Draw resized image
      ctx.drawImage(img, 0, 0, width, height);

      // Convert to JPEG with 95% quality for excellent visual quality
      const compressedBase64 = canvas.toDataURL('image/jpeg', 0.95);
      resolve(compressedBase64);
    };

    img.onerror = () => reject(new Error('Failed to load image'));
    reader.onerror = () => reject(new Error('Failed to read file'));
    
    reader.readAsDataURL(file);
  });
};

/**
 * Compresses and resizes an image for AI analysis
 * - Resizes longest side to ~2048px
 * - JPEG quality 75-80%
 * - Reduces tokens by ~75% vs full-res
 */
export const compressImageForAnalysis = async (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.src = e.target?.result as string;
    };

    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      // Calculate new dimensions (max 2048px on longest side)
      const maxSize = 2048;
      let width = img.width;
      let height = img.height;

      if (width > height) {
        if (width > maxSize) {
          height = Math.round((height * maxSize) / width);
          width = maxSize;
        }
      } else {
        if (height > maxSize) {
          width = Math.round((width * maxSize) / height);
          height = maxSize;
        }
      }

      canvas.width = width;
      canvas.height = height;

      // Draw resized image
      ctx.drawImage(img, 0, 0, width, height);

      // Convert to JPEG with 75% quality
      const compressedBase64 = canvas.toDataURL('image/jpeg', 0.75);
      resolve(compressedBase64);
    };

    img.onerror = () => reject(new Error('Failed to load image'));
    reader.onerror = () => reject(new Error('Failed to read file'));
    
    reader.readAsDataURL(file);
  });
};

/**
 * Loads an image from a source URL
 * @param src - The image source URL or data URL
 * @returns A Promise that resolves to the loaded HTMLImageElement
 */
export function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });
}

/**
 * Crops and compresses an image from a crop area for avatar/profile pictures
 * - Resizes to max 400x400px (avatars are typically small)
 * - JPEG quality 85%
 * - Significantly reduces file size while maintaining visual quality
 * 
 * @param imageSrc - The source image data URL
 * @param cropArea - The area to crop (x, y, width, height in pixels)
 * @param maxWidth - Maximum width for the output (default: 400)
 * @param maxHeight - Maximum height for the output (default: 400)
 * @param quality - JPEG quality 0-1 (default: 0.85)
 * @returns A Promise that resolves to the cropped and compressed image blob
 */
export async function cropAndCompressImage(
  imageSrc: string,
  cropArea: { x: number; y: number; width: number; height: number },
  maxWidth: number = 400,
  maxHeight: number = 400,
  quality: number = 0.85
): Promise<Blob> {
  // Load the source image
  const image = await loadImage(imageSrc);
  
  // Create a temporary canvas for cropping
  const cropCanvas = document.createElement("canvas");
  const cropCtx = cropCanvas.getContext("2d");

  if (!cropCtx) {
    throw new Error("Failed to get canvas context");
  }

  // Calculate target dimensions while maintaining aspect ratio
  let targetWidth = cropArea.width;
  let targetHeight = cropArea.height;
  
  if (targetWidth > maxWidth || targetHeight > maxHeight) {
    const ratio = Math.min(maxWidth / targetWidth, maxHeight / targetHeight);
    targetWidth = Math.round(targetWidth * ratio);
    targetHeight = Math.round(targetHeight * ratio);
  }

  // Set canvas size to the target dimensions (resized)
  cropCanvas.width = targetWidth;
  cropCanvas.height = targetHeight;

  // Draw the cropped and resized image in one operation
  cropCtx.drawImage(
    image,
    cropArea.x,
    cropArea.y,
    cropArea.width,
    cropArea.height,
    0,
    0,
    targetWidth,
    targetHeight
  );

  // Convert to blob with compression
  return new Promise<Blob>((resolve, reject) => {
    cropCanvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("Failed to create compressed blob"));
        }
      },
      "image/jpeg",
      quality
    );
  });
}
